import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { NavDropdown } from "../../common/interfaces/routing";
import { DROPDOWN_KEYS, DROPDOWN_LABELS, ROUTE_LABELS, ROUTE_PATHS } from "../../common/enums/routing-enums";
import TEALIUM from "../../common/enums/tealium-enums";
import { track } from "../../common/util/tealium";
import { hasNativeBridge, openInNativeBrowser } from "../../common/util/nativeBridge";
import { useAppSelector } from "../../store";

const ACADEMY_FRAME_NAME = "academyFrame";

/**
 * Checks if a route label is for Academy
 */
export const isAcademyRoute = (label: string): boolean => label === ROUTE_LABELS.ACADEMY;

/**
 * Checks if Academy link should be displayed based on menu config
 */
export const shouldDisplayAcademyLink = (menuConfig: NavDropdown[]): boolean => {
  const academy = menuConfig.find((dropdown) => dropdown.key === DROPDOWN_KEYS.ACADEMY);
  return academy?.label === DROPDOWN_LABELS.ACADEMY && !academy?.skip;
};

/**
 * Gets the Academy link URL from menu config
 */
export const getAcademyLink = (menuConfig: NavDropdown[]): string => {
  const academy = menuConfig.find((dropdown) => dropdown.key === DROPDOWN_KEYS.ACADEMY);
  return academy?.path ?? "";
};

/**
 * Tracks Academy link click in Tealium
 */
export const trackAcademyClick = (): void => {
  track(TEALIUM.U_TAG_METHOD.LINK, TEALIUM.EVENT_NAME.CLICK_ACTION, "", TEALIUM.CATEGORY.ACADEMY, "", "academy");
};

/**
 * Opens Academy URL using the best available method:
 * - If inside a native app webview, uses H5 bridge to open in device browser (Safari/Chrome)
 * - Otherwise falls back to window.open in a named frame (reuses existing window by name)
 *
 * Note: Callers are responsible for tracking (trackAcademyClick) before invoking this.
 */
export const openAcademyWindow = (url: string): Window | null => {
  if (hasNativeBridge()) {
    openInNativeBrowser(url);
    return null;
  }
  return window.open(url, ACADEMY_FRAME_NAME);
};

/**
 * Gets the Academy frame target name for anchor tags
 */
export const getAcademyFrameTarget = (): string => ACADEMY_FRAME_NAME;

/**
 * Custom hook for managing Academy link behavior
 * When useAuthRedirect is enabled, navigates to auth page
 * Otherwise opens the URL directly in a named window (reuses same tab)
 */
export const useAcademyLink = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { configuration } = useAppSelector((state) => state.boot);
  const useAuthRedirect = configuration?.externalIntegration?.academy?.useAuthRedirect ?? false;

  const handleAcademyClick = useCallback(
    (url: string, event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      // Prevent navigation if already on auth page
      if (window.location.pathname === ROUTE_PATHS.ACADEMY_AUTH) {
        return;
      }

      if (useAuthRedirect) {
        navigate(ROUTE_PATHS.ACADEMY_AUTH, { state: { from: location.pathname } });
      } else {
        trackAcademyClick();
        openAcademyWindow(url);
      }
    },
    [useAuthRedirect, navigate, location.pathname],
  );

  return {
    handleAcademyClick,
  };
};
